import { TenantListImportPage } from 'ClientPortal/src/pages/Playground/TenantListImport/TenantListImportPage'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import ErrorPage from 'ClientPortal/src/pages/ErrorPage'
import { AssetListPage } from 'ClientPortal/src/pages/Assets/AssetList/AssetListPage'
import { OrderListPage } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPage'
import { AnalysisPage } from 'ClientPortal/src/pages/Analysis/AnalysisPage'
import { Dashboard } from 'ClientPortal/src/pages/Dashboard/Dashboard'
import { AuthenticatedAppWrapper } from 'ClientPortal/src/pages/AuthenticatedAppWrapper'
import { CreateOrderPage } from 'ClientPortal/src/pages/Orders/CreateOrder/CreateOrderPage'
import UserDetailsPage from 'ClientPortal/src/pages/Settings/UserSettingsPage'
import { Layout, LayoutWrapper } from 'ClientPortal/src/pages/Layout'
import { Welcome } from 'ClientPortal/src/pages/Welcome'
import { DYNAMIC_APP_PATHS, STATIC_APP_PATHS, WELCOME_PAGE_PATH } from 'ClientPortal/src/router/constants'
import { OrderDetailsPage } from 'ClientPortal/src/pages/Orders/OrderDetails/OrderDetailsPage'
import { TasksOverviewPage } from 'ClientPortal/src/pages/Orders/TaskOverview/TaskOverviewPage'
import { ThreadListPage } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/ThreadListPage'
import { PrivacyPolicyPage } from 'ClientPortal/src/pages/Privacy/PrivacyPolicyPage'
import { ImprintPage } from 'ClientPortal/src/pages/Privacy/ImprintPage'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { MarketDataPage } from 'ClientPortal/src/pages/MarketData/MarketDataPage'
import { ThreadOverviewPage } from 'ClientPortal/src/pages/ThreadOverview/ThreadOverviewPage'
import { NotificationsSettingsPage } from 'ClientPortal/src/pages/Settings/NotificationSettingsPage'
import { TaskListPage } from 'ClientPortal/src/pages/Orders/OrderDetails/TaskList/TaskListPage'
import ValuationPageLayout from '../pages/Assets/./AssetDetails/Valuation/ValuationLayout/ValuationPageLayout'
import ValuationOverview from '../pages/Assets/./AssetDetails/Valuation/ValuationOverview/ValuationOverview'
import ValuationIncomePage from '../pages/Assets/./AssetDetails/Valuation/ValuationIncomePage/ValuationIncomePage'
import ValuationMarketDataPage from '../pages/Assets/./AssetDetails/Valuation/ValuationMarketData/ValuationMarketDataPage'
import ValuationDCFPage from '../pages/Assets/./AssetDetails/Valuation/ValuationDCF/ValuationDCFPage'
import ValuationTenantListPage from '../pages/Assets/AssetDetails/Valuation/ValuationTenantList/ValuationTenantListPage'
import AssetPageLayout from '../pages/Assets/./AssetDetails/AssetPageLayout/AssetPageLayout'
import AssetValuationList from '../pages/Assets/./AssetDetails/AssetValuationList/AssetValuationList'
import AssetDeviationAnalysis from '../pages/Assets/./AssetDetails/AssetDeviationAnalysis/AssetDeviationAnalysis'
import { AssetKeyValues } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/AssetKeyValues'
import ValuationPlausibility from '../pages/Assets/AssetDetails/Valuation/ValuationPlausibility/ValuationPlausibility'
import ValueCheckPageLayout from '../pages/Assets/AssetDetails/ValueCheck/ValueCheckLayout/ValueCheckPageLayout'
import { ValXmlExtractorPage } from 'ClientPortal/src/pages/Playground/ValXmlExtractorPage'
import { PdfValidatorPage } from 'ClientPortal/src/pages/Playground/PdfValidatorPage'
import { redirectExternalParticipantPath, redirectLegacyExternalOrderPath } from 'ClientPortal/src/router/externalDataroomRedirect'

const MarketDataOrAnalysisPage = () => {
    const { isMarketDataEnabled } = useFlags()
    return isMarketDataEnabled ? <MarketDataPage /> : <AnalysisPage />
}

const valuationSubRoutes = [
    {
        path: 'overview',
        element: <ValuationOverview />,
    },
    {
        path: 'income',
        element: <ValuationIncomePage />,
    },
    {
        path: 'dcf',
        element: <ValuationDCFPage />,
    },
    {
        path: 'tenant-list',
        element: <ValuationTenantListPage />,
    },
    {
        path: 'market-data',
        element: <ValuationMarketDataPage />,
    },
    {
        path: '',
        element: <Navigate to="overview" replace />,
    },
]

const assetSubRoutes = [
    {
        path: 'overview',
        element: <AssetKeyValues />,
    },
    {
        path: 'valuations',
        element: <AssetValuationList />,
    },
    {
        path: 'deviation-analysis',
        element: <AssetDeviationAnalysis />,
    },
    {
        path: '',
        element: <Navigate to="overview" replace />,
    },
]

const routesBelowAuthenticator = [
    {
        errorElement: <ErrorPage />,
        children: [
            {
                element: <LayoutWrapper />,
                errorElement: (
                    <Layout>
                        <ErrorPage />
                    </Layout>
                ),
                children: [
                    {
                        path: WELCOME_PAGE_PATH,
                        element: <Welcome />,
                    },
                    {
                        // Redirect index to /dashboard
                        index: true,
                        element: <Navigate to={STATIC_APP_PATHS.dashboard} replace />,
                    },
                    {
                        path: STATIC_APP_PATHS.dashboard,
                        element: <Dashboard />,
                    },
                    { path: STATIC_APP_PATHS.userDetailsPage, element: <UserDetailsPage /> },
                    { path: STATIC_APP_PATHS.notificationSettingsPage, element: <NotificationsSettingsPage /> },
                    { path: STATIC_APP_PATHS.threadOverviewPage, element: <ThreadOverviewPage /> },
                    {
                        path: STATIC_APP_PATHS.assetListPage,
                        element: <AssetListPage />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.assetDetailsPage,
                        element: <AssetPageLayout />,
                        children: assetSubRoutes,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.valuationDetailsPage,
                        element: <ValuationPageLayout />,
                        children: valuationSubRoutes,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.valueCheckDetailsPage,
                        element: <ValueCheckPageLayout />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.valuationPlausibilityPage,
                        element: <ValuationPlausibility />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.createOrderPage,
                        element: <CreateOrderPage isCreatingFromAsset />,
                    },
                    {
                        path: STATIC_APP_PATHS.orderListPage,
                        element: <OrderListPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.taskOverviewPage,
                        element: <TasksOverviewPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.createOrderPage,
                        element: <CreateOrderPage />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.orderDetailsPage,
                        element: <OrderDetailsPage />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.orderThreadListPage,
                        element: <ThreadListPage />,
                    },
                    {
                        path: DYNAMIC_APP_PATHS.orderTaskListPage,
                        element: <TaskListPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.analysisPage,
                        element: <MarketDataOrAnalysisPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.playgroundValXmlExtractor,
                        element: <ValXmlExtractorPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.playgroundTenantListImport,
                        element: <TenantListImportPage />,
                    },
                    {
                        path: STATIC_APP_PATHS.playgroundPdfValidator,
                        element: <PdfValidatorPage />,
                    },
                ],
            },
        ],
    },
]

const routes = [
    {
        path: 'external-participants/*',
        loader: redirectExternalParticipantPath,
    },
    {
        path: 'orders/:orderId/data-room',
        loader: redirectLegacyExternalOrderPath,
    },
    {
        path: 'imprint',
        element: <ImprintPage />,
    },
    {
        path: 'impressum',
        element: <ImprintPage />,
    },
    {
        path: 'datenschutz',
        element: <PrivacyPolicyPage locale="de-DE" />,
    },
    {
        path: 'privacy',
        element: <PrivacyPolicyPage locale="en-US" />,
    },
    {
        path: '/',
        element: <AuthenticatedAppWrapper />,
        errorElement: <ErrorPage />,
        children: routesBelowAuthenticator,
    },
]
const router = createBrowserRouter(routes)

export { router, routesBelowAuthenticator }
